import React, { useState } from "react"
import { Form, Dropdown } from "react-bootstrap";
import caretdown from "../../../assets/images/caretdown.svg";
import crossIcon from "../../../assets/images/cross-icon.svg";

export type OptionType = {
    id: string | number;
    label: string;
    value: string;
}

type SelectFieldType = {
    data: Array<OptionType>;
    selectedValue?: string[] | number[] | string | number | OptionType[] | OptionType;
    onChange: (value: string | number, option: OptionType) => void;
    withSearch?: boolean;
    multiselect?: boolean;
    label: string;
    disabled?: boolean;
    onSearch?: (value: string) => Promise<void>;
    as?: "typehead" | "select";
    searchStatus?: "loading" | "error" | "success";
};

type DropDownItemType = {
    option: OptionType;
    isSearch?: boolean;
    handleSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onChange?: (value: string | number, option: OptionType) => void;
    searchValue?: string;
    index?: number
} & Pick<SelectFieldType, "as" | "multiselect" | "selectedValue">

type DropDownToggleType = {
    selectedInputValue?: any;
    handleSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
} & Pick<SelectFieldType, "as" | "disabled" | "label">

const DropDownToggle = ({ label, disabled, as, handleSearch, selectedInputValue }: DropDownToggleType) => {
    if(as === "typehead") {
        return (
            <Dropdown.Toggle 
                className="form-control"
                as="input" 
                disabled={disabled}
                placeholder={label}
                // @ts-ignore
                type="search" 
                // @ts-ignore
                onChange={handleSearch}
            />
        )
    }

    return (
        <Dropdown.Toggle className="custom-dropdown-toggle"  disabled={disabled}>
            {selectedInputValue ? <span style={{color: "#41505f"}}>{selectedInputValue}</span> : <span>{label}</span>}
            <img src={caretdown} alt="Dropdown icon" />
        </Dropdown.Toggle>
    )
}

const DropDownItem = ({ option, isSearch, handleSearch, onChange, multiselect, selectedValue, as, searchValue = "", index }: DropDownItemType) => {
    const { id, label } = option;

    const handleChange = (e: any) => {
        e.preventDefault()
        onChange?.(id, option);
    }

    const preventDetault = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
    }
    const isChecked = Array.isArray(selectedValue) ? selectedValue.some(selected => (selected as OptionType)?.label === option.label || selected === option.label) : selectedValue === option.label;
    if(isSearch) {
        return (
            <Dropdown.Item 
                as="input" 
                type="search" 
                placeholder={option.label}
                eventKey={index} 
                active={isChecked}
                onClick={preventDetault} 
                onChange={handleSearch}
            />
        )
    }

    if(multiselect && as !== "typehead") {
        const Label = () => <span style={{ cursor: "pointer", marginLeft: "5px" }} onClick={handleChange}>{label}</span>
        const isChecked = Array.isArray(selectedValue) ? selectedValue.some(selected => (selected as OptionType)?.label === label || selected === label) : selectedValue === label;
        return (
            <Dropdown.Item as="button" eventKey={index}  onClick={preventDetault} active={isChecked}>
                <Form.Check
                    type="checkbox"
                    id={`dropdown-${id}`}
                    onChange={handleChange}
                    checked={isChecked}
                    className="fscclaim-dropdown"
                    label={<Label />}
                />
            </Dropdown.Item>
        )
    }

    if(as === "typehead") {
        const matchIndex = label.toLowerCase().indexOf(searchValue.toLowerCase());
        const isChecked = Array.isArray(selectedValue) ? selectedValue.some(selected => (selected as OptionType)?.value === option.value || selected === option.value) : selectedValue === option.value;
        return (
            <Dropdown.Item as="button" style={{marginTop: "3px"}} eventKey={index}  onClick={handleChange} active={isChecked}>
                {label.slice(0, matchIndex)}
                <span style={{ fontWeight: "bold" }}>{label.slice(matchIndex, matchIndex + searchValue.length)}</span>
                {label.slice(matchIndex + searchValue.length)}
            </Dropdown.Item>
        )
    }

    return (
        <Dropdown.Item as="button" eventKey={index} active={label === selectedValue} onClick={handleChange}>
            {label}
        </Dropdown.Item>
    )
}

const SelectedPill = ({ selected, data, onChange, disabled }: { disabled?: boolean; selected: string | number, data: any, onChange: (value: string | number, option: OptionType) => void }) => {
    const selectedOption = data;
    if(!selectedOption) return null;

    const handleClick = (e: any) => {
        e.preventDefault();
        onChange?.(selected, selectedOption);
    }

    return (
        <div key={selected} className={`custom-dropdown-selected-pill ${selected ? "mb-1" : ""}`}>
            <span className="me-2">{selectedOption.label ?? selectedOption}</span>
            <button className="custom-dropdown-selected-pill-close-btn" onClick={handleClick} disabled={disabled} style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
                <img src={crossIcon} alt="Close" />
            </button>
        </div>
    )
}

export default function SelectField(props: SelectFieldType) {
    const [searchValue, setSearchValue] = useState("");
    const { label, data, selectedValue, onChange, withSearch, onSearch, disabled, multiselect, as, searchStatus} = props;

    const showMenu = (!withSearch && !!data.length) || !!data.length;
    const selectedValueLabel = Array.isArray(selectedValue) ? selectedValue.join(", ") : selectedValue?.toString();
    const selectedInputValue = !Array.isArray(selectedValue) ? selectedValue?.toString() : null;
    const toggleLebel = as === "typehead" || multiselect ? label : (selectedValueLabel || label);

    const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        onSearch?.(value);
    }
    
    return (
        <Dropdown>
            {Array.isArray(selectedValue) && selectedValue && (
                <div className="custom-dropdown-selected-pill-container">
                    {selectedValue.map((value: any) => (
                        <SelectedPill key={value.id} selected={value.label} data={value} onChange={onChange} disabled={disabled} />
                    ))}
                </div>
            )}
            <DropDownToggle {...{ disabled, as, label: toggleLebel, handleSearch, selectedInputValue }} />
            {showMenu && <Dropdown.Menu className="custom-dropdown-menu">
                {withSearch && <DropDownItem isSearch option={{ id: "search", label: "Search", value: "" }} handleSearch={handleSearch} />}
                {searchStatus === "loading" && <Dropdown.Item disabled>Loading...</Dropdown.Item>}
                {searchStatus === "error" && <Dropdown.Item disabled>Something went wrong</Dropdown.Item>}
                {data.map((option, index) => (
                    <DropDownItem {...{ as, multiselect, selectedValue, onChange, option, searchValue, index }} key={option.id} />
                ))}
            </Dropdown.Menu>}
        </Dropdown>
    );
}